import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Check, ConciergeBell, Copy, Plane, Scale } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { policyScopeLabel } from '@/components/pnr/flight-resolve-panel';
import type { CaseOverviewSummary } from './use-case-overview';
import { caseLabelParts } from './case-label';
import { policyReasonText } from '@/lib/policy-reason/policy-reason';

// Friendly case header (frontend-conventions §18.3): the primary title is human
// data derived from the case — flight · airport · date · pax — NOT the URN. The
// URN is shown once, small + muted, with a copy button. Never the title, never
// duplicated.
//
// `aside` is the case's numbers + its case-level action (CaseStatStrip), passed
// in rather than fetched here so this stays a pure render of the overview it is
// given — the Overview tab it replaces is gone.

interface CaseHeaderProps {
  caseUrn: string;
  data: CaseOverviewSummary | undefined;
  isLoading: boolean;
  isError: boolean;
  /** Right-hand slot: the stat strip + Resolve. Optional — tests render without it. */
  aside?: ReactNode;
}

export function CaseHeader({ caseUrn, data, isLoading, isError, aside }: CaseHeaderProps) {
  const { t } = useTranslation('caseDetail');
  const [copied, setCopied] = useState(false);
  // The resolver's reason in words — an unrecognised one is not shown (never raw English).
  const policyWhy = policyReasonText(data?.policyMatchReason, (k) => String(t(k)));

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard?.writeText(caseUrn);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable — no-op */
    }
  };

  if (isLoading) {
    return (
      <header className="border-b border-border bg-background px-4 py-3 sm:px-6">
        <Skeleton className="h-6 w-64" />
        <Skeleton className="mt-2 h-3 w-48" />
      </header>
    );
  }

  // A case opened over hand-picked PNRs has no flight, so `caseLabelParts` has no
  // flight number to lead with. Say what it IS rather than letting it read as a
  // flight case whose flight failed to load.
  const parts = caseLabelParts(data);
  if (data && !data.flightUrn) parts.unshift(t('header.pnrCase'));
  const label = parts.length > 0 ? parts.join(' · ') : t('header.fallbackLabel');
  const status = typeof data?.status === 'string' ? data.status : undefined;

  return (
    <header className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-border bg-background px-4 py-3 sm:px-6">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="flex items-center gap-2 text-base font-semibold sm:text-lg">
            <Plane className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
            <span className="truncate">{label}</span>
          </h1>
          {status ? (
            <span
              className="rounded bg-muted px-2 py-0.5 text-xs uppercase tracking-wide text-muted-foreground"
              data-testid="case-status-badge"
            >
              {status}
            </span>
          ) : null}
          {/* Care-only: no flight rebooking on this case — said where the case
              is named, so nobody hunts for the missing rebooking column. */}
          {data?.rebookingDisabled ? (
            <Badge variant="info" size="sm" data-testid="case-care-only-badge">
              <ConciergeBell className="h-3 w-3" aria-hidden />
              {t('header.careOnly')}
            </Badge>
          ) : null}
          {isError ? (
            <span className="text-xs text-destructive">{t('header.detailsUnavailable')}</span>
          ) : null}
        </div>
        {/* URN — secondary, muted, shown once, copyable. */}
        <button
          type="button"
          onClick={() => void copy()}
          title={t('header.copyUrn')}
          className="mt-1 inline-flex max-w-full items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          <span className="truncate font-mono">{caseUrn}</span>
          {copied ? (
            <Check className="h-3 w-3 shrink-0 text-emerald-500" aria-hidden />
          ) : (
            <Copy className="h-3 w-3 shrink-0" aria-hidden />
          )}
          <span className="sr-only">{t('header.copyUrn')}</span>
        </button>
        {/* Governing-policy provenance (POL-12) — which policy governs this case
            and WHY it won, so the operator isn't guessing across the catalog. */}
        {data?.governingPolicyUrn || data?.policyMatchType ? (
          <p
            className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground"
            data-testid="case-governing-policy"
            {...(data.governingPolicyUrn ? { title: data.governingPolicyUrn } : {})}
          >
            <Scale className="h-3 w-3 shrink-0" aria-hidden />
            <span>
              {t('header.governedBy', { scope: policyScopeLabel(data.policyMatchType ?? undefined) })}
              {policyWhy ? (
                <span className="text-muted-foreground/80" data-testid="case-governing-reason">
                  {' '}
                  — {policyWhy}
                </span>
              ) : null}
            </span>
            {data.governingPolicyUrn ? (
              <Link
                to={`/policies/${encodeURIComponent(data.governingPolicyUrn)}`}
                className="text-primary hover:underline"
              >
                {t('header.viewPolicy')}
              </Link>
            ) : null}
          </p>
        ) : null}
      </div>
      {aside ? <div className="shrink-0">{aside}</div> : null}
    </header>
  );
}
